import * as React from 'react';
import {
  MdAdd,
  MdSearch,
  MdClose,
  MdCheckCircle,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdPauseCircle,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { getAllLocations, deleteLocation } from '../../Services/LocationService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
};

const getStatusLabel = (row) => {
  if (row.status_name) return row.status_name;
  if (row.StatusName) return row.StatusName;
  return row.status_id === 1 ? 'Active' : 'Inactive';
};

export default function LocationList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_VIEW, decodedToken);
  const canCreateLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_CREATE, decodedToken);
  const canEditLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_EDIT, decodedToken);
  const canDeleteLocation = permissionHas(userPermissionCodes, PermissionCodes.LOCATIONS_DELETE, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const handleCreateLocation = React.useCallback(() => {
    navigate(APPRoutes.LOCATIONS_FORM);
  }, [navigate]);

  const loadLocations = React.useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await getAllLocations(token);
      const payload = response?.is_success ? response.data ?? response : response;
      const locations = Array.isArray(payload?.locations) ? payload.locations : [];
      const stats = payload?.stats || { total: 0, active: 0, inactive: 0 };

      setRows(
        locations.map((location) => ({
          ...location,
          status_name: getStatusLabel(location),
          organization_name: location.organization_name || location.organizationName || '-',
          account_name: location.account_name || location.accountName || '-',
          name: location.name || location.Name || '-',
        })),
      );
      setStatusSummary({
        total: Number(stats.total || 0),
        active: Number(stats.active || 0),
        inactive: Number(stats.inactive || 0),
      });
    } catch (error) {
      console.error('Failed to load locations:', error);
      toast.error('Unable to fetch locations. Please try again.');
      setRows([]);
      setStatusSummary({ total: 0, active: 0, inactive: 0 });
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadLocations();
    }
  }, [token, loadLocations]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }
    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.id || !token) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');
    try {
      const response = await deleteLocation(deleteTarget.id, token);
      if (response?.is_success) {
        await loadLocations();
        setDeleteTarget(null);
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else {
        setDeleteError(response?.message || 'Failed to delete the location. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the location.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadLocations]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus =
        status === 'All' || row.status_name === status;
      const matchesSearch =
        !term ||
        row.name?.toLowerCase().includes(term) ||
        row.organization_name?.toLowerCase().includes(term) ||
        row.account_name?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Total Locations',
        value: statusSummary.total,
        icon: <MdAdd size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active',
        value: statusSummary.active,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive',
        value: statusSummary.inactive,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ],
    [statusSummary],
  );

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'name',
        label: 'Location',
        minWidth: 220,
        format: (value, row) => canViewLocation ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() => navigate(APPRoutes.LOCATIONS_VIEW.replace(':id', row.id), { state: { location: row } })}
          >
            <div>
              <div className="fw-semibold">{value || '-'}</div>
              <div className="small text-secondary">{row.address_id ? `Address ID: ${row.address_id}` : 'No address details'}</div>
            </div>
          </button>
        ) : (
          <div>
            <div className="fw-semibold text-dark">{value || '-'}</div>
            <div className="small text-secondary">{row.address_id ? `Address ID: ${row.address_id}` : 'No address details'}</div>
          </div>
        ),
      },
      {
        id: 'organization_name',
        label: 'Organization',
        minWidth: 180,
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'account_name',
        label: 'Client',
        minWidth: 180,
      });
    }

    cols.push(
      {
        id: 'status_name',
        label: 'Status',
        minWidth: 120,
        align: 'center',
        format: (value) => (
          <span
            className="badge rounded-pill fw-bolder px-3 py-2 text-center"
            style={{
              minWidth: '96px',
              fontWeight: 1000,
              ...(STATUS_BADGE_STYLE[value] || {
                backgroundColor: 'var(--hc-status-inactive-bg)',
                color: 'var(--hc-status-inactive-text)',
              }),
            }}
          >
            {value}
          </span>
        ),
      },
    );

    if (canViewLocation || canEditLocation || canDeleteLocation) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewLocation ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => navigate(APPRoutes.LOCATIONS_VIEW.replace(':id', row.id), { state: { location: row } })}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditLocation ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(APPRoutes.LOCATIONS_EDIT.replace(':id', row.id))}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteLocation ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleDeleteClick(row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    handleDeleteClick,
    canViewLocation,
    canEditLocation,
    canDeleteLocation,
    isSuperAdmin,
  ]);

  return (
    <>
      <PageTitle title="Locations - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Locations Management</h4>
          <p className="text-secondary mb-0 small">
            Manage all locations and review organization client assignments.
          </p>
        </div>
        {canCreateLocation ? (
          <button
            onClick={handleCreateLocation}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Location</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 44, height: 44, borderRadius: 12 }} />
                <div className="w-100">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 24, width: '45%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search location, organization or client"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">

               <SearchableDropdown
                  placeholder="Select status"
                  options={STATUS_DROPDOWN_OPTIONS}
                  value={status}
                  onChange={setStatus}
                  showSearch={true}
                />

            {/* <FormControl fullWidth size="small">
              <InputLabel id="location-status-label">Status</InputLabel>
              <Select
                labelId="location-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Location"
        entityLabel="location"
        itemName={deleteTarget?.name || ''}
        description="You are about to remove this location from the platform. This action is permanent."
        confirmText="Delete Location"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
